// The 20-level ladder. Levels are per-track (a user can be L8 Security
// and L5 Cloud at the same time) except `governanceLevel`, which is
// platform-wide and only relevant L11+.
//
// Anchor points the codebase already depends on:
//   L5  = first expert-track gate (was EXPERT_REVIEW_MIN_LEVEL)
//   L11 = Guru (was GURU_LEVEL)
//   L20 = Sovereign — constitutionally capped at 3
//
// Anything between those anchors is filled in to give every promotion
// a meaningful name and a meaningful capability change.

export const LEVELS = Object.freeze({
  NOVICE: 1,
  APPRENTICE: 2,
  INITIATE: 3,
  ADEPT: 4,
  PRACTITIONER: 5,           // first expert gate — application + admin review
  REVIEWER: 6,               // paid marketplace eligibility
  SENIOR_REVIEWER: 7,        // paid whole-report reviews; can propose questions
  PRACTITIONER_ARCHITECT: 8, // can author derivation rules + adjudicate
  LEAD_PRACTITIONER: 9,      // peer-elected; first Track Council vote
  TRACK_OWNER: 10,           // admin-appointed; one per track
  GURU: 11,                  // invitation only; cross-track authority
  SAGE: 12,                  // Catalog Senate
  ARCHITECT: 13,             // catalog-version cutovers
  PRINCIPAL: 14,             // platform-direction council
  DISTINGUISHED: 15,         // tenure-based; spokesperson
  PATRON: 16,                // funding/IP contribution
  ELDER: 17,                 // final-tier arbitration
  CUSTODIAN: 18,             // constitutional custody
  MAGISTER: 19,              // elected super-admin equivalent
  SOVEREIGN: 20              // ultimate authority; max 3 ever
});

// Map level number → name, for UI display + auditing.
export const LEVEL_NAMES = Object.freeze(
  Object.fromEntries(Object.entries(LEVELS).map(([name, n]) => [n, name]))
);

// What the user can actually do at each level. These are the gates the
// rest of the codebase checks against — never check a level by its number
// in business logic, check it via these named gates (so renaming a level
// or re-numbering can happen without code rewrites).

// Member-only stage (no track required).
export const MEMBER_MIN = LEVELS.NOVICE;

// First expert capabilities — calibration reviews, peer rating, answer
// validation. Anything where a track grant is required to participate.
export const EXPERT_ENTRY = LEVELS.PRACTITIONER;             // 5

// Eligible to appear in the paid marketplace + take section reviews.
export const MARKETPLACE_ELIGIBLE = LEVELS.REVIEWER;         // 6

// Whole-report reviewing; can propose new questions to the catalog.
export const WHOLE_REPORT_REVIEW = LEVELS.SENIOR_REVIEWER;   // 7

// Can author derivation rules; can adjudicate disputed reviews.
export const RULE_AUTHOR = LEVELS.PRACTITIONER_ARCHITECT;    // 8

// ── The level a review REQUEST asks of its reviewer, per tier ──────────
//
// The dashboard's request store carried these as bare numbers (6 / 7 / 8)
// and nothing else in the product could see them — so the admin approval
// page offered "Approve as L5 · Expert" for a track whose plan-included
// reviews need L7. The reviewer was granted, the queue stayed empty, and
// neither screen said why. One place for the floors, read by both.
//
//   community    — best-effort, unpaid: marketplace entry level.
//   subscription — plan-included whole-report review.
//   urgent       — priced, SLA-backed: one rung above.
export const REVIEW_TIER_LEVEL_FLOOR = Object.freeze({
  community:    MARKETPLACE_ELIGIBLE,   // 6
  subscription: WHOLE_REPORT_REVIEW,    // 7
  urgent:       RULE_AUTHOR             // 8
});

// The lowest level at which a track grant can claim ANY marketplace review.
export const LOWEST_CLAIMABLE_LEVEL = Math.min(...Object.values(REVIEW_TIER_LEVEL_FLOOR));

// Track-council vote weight starts here.
export const TRACK_COUNCIL_MEMBER = LEVELS.LEAD_PRACTITIONER; // 9

// Track ownership — veto rights on track-scoped catalog changes.
export const TRACK_OWNER = LEVELS.TRACK_OWNER;               // 10

// Cross-track authority — Guru tier.
export const CROSS_TRACK = LEVELS.GURU;                      // 11

// Governance-only stages — no commercial throughput required.
export const GOVERNANCE_ENTRY = LEVELS.SAGE;                 // 12
export const CONSTITUTIONAL = LEVELS.CUSTODIAN;              // 18
export const SUPREME = LEVELS.SOVEREIGN;                     // 20

// Take-rate by level — the expert keeps this fraction of every paid review.
// (The remainder is the platform fee.) Founder-cohort bonus stacks +5%
// on top, capped at 95%.
export const TAKE_RATE_BY_LEVEL = Object.freeze({
  // expert tier
  5: 0.80,
  6: 0.80,
  7: 0.82,
  8: 0.82,
  9: 0.85,
  10: 0.85,
  11: 0.88,
  // governance tier — typically don't take reviews, but if they do, max rate
  12: 0.88,
  13: 0.88,
  14: 0.88,
  15: 0.88,
  16: 0.88,
  17: 0.88,
  18: 0.88,
  19: 0.88,
  20: 0.88
});

// Track Council vote weight (only L9+ vote; weight scales with level).
export const COUNCIL_VOTES_BY_LEVEL = Object.freeze({
  9: 1, 10: 2, 11: 3, 12: 3, 13: 3, 14: 3,
  15: 3, 16: 3, 17: 3, 18: 3, 19: 3, 20: 3
});

// Promotion mode — how a user advances to this level.
//   'auto'     = nightly job promotes when criteria met
//   'peer'     = peer-elected within a track (annual cycle)
//   'admin'    = manager / admin appointment
//   'invite'   = invitation only
export const PROMOTION_MODE = Object.freeze({
  1: 'auto', 2: 'auto', 3: 'auto', 4: 'auto',
  5: 'admin',          // application + manager approval
  6: 'auto',           // 3 calibrations cleared
  7: 'auto',           // 30 paid reviews ≥ 4.0 avg
  8: 'admin',          // manual quality review at this tier
  9: 'peer',           // track election
  10: 'admin',         // track-owner appointment
  11: 'invite',        // Guru — never grant casually
  12: 'invite', 13: 'invite', 14: 'invite',
  15: 'invite', 16: 'invite', 17: 'invite',
  18: 'invite', 19: 'invite', 20: 'invite'
});

// Take-rate for a given (level, founderCohort) pair.
// Returns the expert's share (0..1); platform fee is 1 - this.
// Rounded to whole-percent precision — take-rates are never sub-percent
// in the real world, and rounding avoids IEEE-754 surprises.
export const takeRateFor = (level, founderCohort = false) => {
  const base = TAKE_RATE_BY_LEVEL[level] ?? 0;
  if (!base) return 0;
  const bonus = founderCohort ? 0.05 : 0;
  return Math.round(Math.min(0.95, base + bonus) * 100) / 100;
};

// Council vote weight for a given level.
export const councilVotesFor = (level) => COUNCIL_VOTES_BY_LEVEL[level] || 0;

// Is `userLevel` at or above the minimum?
export const meetsLevel = (userLevel, minLevel) => {
  return (Number(userLevel) || 0) >= (Number(minLevel) || 0);
};
